'use client'
import { htmlToSlate } from '@slate-serializers/html'
import { withHistory } from 'slate-history'
import { createContext, useContext, useMemo } from 'react'
import { createEditor, Descendant } from 'slate'
import { Slate, withReact } from 'slate-react'
import { withCustom } from './plugins/with-custom'
import { withLists } from './plugins/with-lists'
import { withTables } from './plugins/with-tables'

export type InitialValue = string | Descendant[]

export type EditorProviderProps = {
  children: React.ReactNode
  initialValue?: InitialValue
  onChange?: (value: string) => void
  onClickAction?: () => Promise<void>
}
const EditorContext = createContext<{
  onClickAction?: () => Promise<void>
}>({
  onClickAction: async () => {},
})

export const useEditorProvider = () => useContext(EditorContext)

export function EditorProvider({
  children,
  initialValue,
  onChange,
  onClickAction,
}: EditorProviderProps) {
  const editor = useMemo(
    () => withTables(withLists(withCustom(withReact(withHistory(createEditor()))))),
    []
  )

  const onSlateChange = (value: Descendant[]) => {
    if (onChange) {
      // Enviamos la estructura Slate serializada como JSON en lugar de HTML
      onChange(JSON.stringify(value))
    }
  }

  return (
    <EditorContext.Provider
      value={{
        onClickAction,
      }}>
      <Slate
        editor={editor}
        initialValue={parseToSlateInitialValue(initialValue)}
        onChange={onSlateChange}>
        {children}
      </Slate>
    </EditorContext.Provider>
  )
}

function parseToSlateInitialValue(initialValue?: Descendant[] | string) {
  try {
    if (!initialValue) {
      return createAnEmptyDescendent()
    }

    if (typeof initialValue === 'string') {
      // Primero, intentamos parsear el string como JSON (estructura Slate)
      try {
        const parsed = JSON.parse(initialValue)
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
        }
      } catch {
        // Si no es JSON válido, continuamos con el proceso normal
      }

      // Si el string contiene HTML, lo convertimos a estructura Slate
      if (containHTMLTags(initialValue)) {
        return htmlToSlate(initialValue)
      }

      // Si es texto plano, lo convertimos a párrafos
      return createDescendentFromInitialValue(initialValue)
    }

    // Verificar que initialValue es un array válido y tiene al menos un elemento
    if (Array.isArray(initialValue) && initialValue.length > 0) {
      // Asumimos que es un array válido de Descendant
      return initialValue
    }

    // Si llegamos aquí, el initialValue no es válido, devolvemos un nodo vacío
    return createAnEmptyDescendent()
  } catch (error) {
    console.error('Error parsing initialValue:', error)
    return createAnEmptyDescendent()
  }
}

function createAnEmptyDescendent() {
  return [{ type: 'p', children: [{ text: '' }] }]
}

function createDescendentFromInitialValue(initialValue: string) {
  return initialValue.split('\n').map((text) => {
    return { type: 'p', children: [{ text }] }
  })
}

function containHTMLTags(value: string) {
  return /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/g.test(value)
}
